<docs>
# Input

> NOTE: this docs defined at **docs** section inside Input.vue module

Input UI Set

<!-- STORY -->

### Example

>Default

```html
<mt-input v-model="value" name="default-input" placeholder="Enter your details here" />
```

>with label

```html
<mt-input v-model="value" name="with-label" placeholder="Enter your details here" label="Field label"/>
```

>with caption

```html
<mt-input v-model="value" name="with-caption" placeholder="Enter your details here" caption="Lorem ipsum dolor sit amet, consectetur adipiscing elit."/>
```

>Invalid

```html
<mt-input v-model="value" name="invalid" :valid="false" placeholder="Enter your details here" caption="Lorem ipsum dolor sit amet, consectetur adipiscing elit."/>
```

>Clearble

```html
<mt-input v-model="value" name="clearble" :clearble="true" placeholder="Enter your details here" caption="Lorem ipsum dolor sit amet, consectetur adipiscing elit."/>
```

>Event on change

```javascript
data(){
  return {
    value: ''
  }
},
methods: {
  handleChange(value, event){
    console.log('value', value, this.value)
  }
}
```

```html
your input : {{value}}
<mt-input v-model="value" name="icon" placeholder="Enter your details here" caption="Lorem ipsum dolor sit amet, consectetur adipiscing elit." icon="search" @change="handleChange"/>
```

>Icon

```html
<mt-input value="" name="icon" placeholder="Enter your details here" caption="Lorem ipsum dolor sit amet, consectetur adipiscing elit." icon="search" :clearble="true"/>
```

>Event on click icon

```html
<mt-input value="" name="icon" placeholder="Enter your details here" caption="Lorem ipsum dolor sit amet, consectetur adipiscing elit." icon="search" @clickIcon="handleClickIcon"/>
```
```javascript
methods: {
  handleClickIcon(event){
    console.log('click icon', event)
  }
}
```

>Textarea

```html
<mt-input value="" type="textarea" name="textarea" placeholder="Enter your details here" caption="Lorem ipsum dolor sit amet, consectetur adipiscing elit."/>
```

#### Props

| Property | Description   | Type     | Accepted Values           | Default     |
|----------|---------------|----------|---------------------------|-------------|
| type     | type of input | String   | text/date/datetime-local/email/file/hidden/month/number/password | text     |
| label    | label         | String   | - | -    |
| name     | name of name  | String   | - | required    |
| placeholder | placeholder   | String  | —    | -       |
| required | whether value is required  | Boolean  | —    | false    |
| disabled | whether input is disabled  | Boolean  | -    | false     |
| caption  | caption   | String   | -   | -     |
| icon     | icon with button | String  | icon name of mt-icon      | —           |
| clearble | whether value is clearble  | Boolean  | -    | false    |
| width    | width  | Number  | -    | 240    |


### Events

| Name         | Description                       | Parameters   |
|--------------|-----------------------------------|--------------|
| change       | fire when checked value changed.  | value, event |

</docs>
<template>
  <div
    :style="{width: width+'px'}"
    :class="`mt-input mt-input--${valid ? 'valid' : 'invalid'} ${disabled ? 'mt-input--disabled': ''}`">
    <label v-on:mouseover="isHover = true" v-on:mouseleave="isHover = false">
      <span class="mt-input__label mt-font--caption20 mt--neutrals-dark-gray-02">{{label}}</span>
      <input
        ref="input"
        v-if="type !== 'textarea'"
        :value="selfModel"
        @keyup="handleInputChange"
        @focus="handleInputFocus"
        @click="handleInputClick"
        :name="name" :id="name"
        :type="type"
        :placeholder="placeholder"
        :required="required"
        :disabled="disabled"
        :style="{width: width+'px'}"
        class="mt-font--body10"/>
      <textarea
        v-if="type === 'textarea'"
        :style="{width: width+'px'}"
        @focus="resizeTextarea"
        @keyup="resizeTextarea"
        class="mt-font--body10"
        :value="selfModel"></textarea>
      <span
        v-if="!valid"
        :style="{width: width+'px'}"
        class="mt-input__invalid-msg mt--bg--error mt--white mt-font--caption10">
        Lorem ipsum dolor sit amet, consectetur
      </span>
      <span
        :style="{width: width+'px'}"
        class="mt-input__caption mt-font--caption10 mt--neutrals-dark-gray-01">{{caption}}</span>
      <mt-icon
        v-if="clearble && selfModel.length > 0"
        @click="handleClickClear"
        class="mt-input__clearble"
        iconName="close-circle"
        :style="clearbleStyle"/>
      <mt-icon
        v-if="icon"
        @click="handleClickIcon"
        class="mt-input__icon"
        :iconName="icon"
        :style="{top: label ? '30px' : '11px'}"/>
    </label>
  </div>
</template>
<script>
export default {
  name: 'mt-input',
  model: {
    prop: 'value',
    event: 'change'
  },
  props: {
    name: {
      type: String,
      required: true,
    },
    value: {
      type: [String, Number],
      default: ''
    },
    label: {
      type: String,
    },
    type: {
      type: String,
      validator: function (value) {
        // The value must match one of these strings
        return ['text', 'textarea', 'date', 'datetime-local', 'email', 'file', 'hidden', 'month', 'number', 'password'].indexOf(value) !== -1
      },
      default: 'text'
    },
    placeholder: {
      type: String,
    },
    required: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    },
    caption: {
      type: String
    },
    width: {
      type: Number,
      default: 240
    },
    valid: {
      type: Boolean,
      default: true
    },
    inValidMsg: {
      type: String,
      default: 'not valid'
    },
    clearble: {
      type: Boolean,
      default: false
    },
    icon: {
      type: String,
    },

  },
  inject: {
    selectOption: {
      type: Object,
      default: function(){
        return {
          focusable: true
        }
      }
    }
  },
  data(){
    return {
      selfModel: '',
      isHover: false,
    }
  },
  computed: {
    clearbleStyle(){
      let style = {top: this.label ? '30px' : '11px', right: '5px'}
      if(this.icon) style.right = '29px'
      return style
    }
  },
  methods: {
    handleClickClear(evt){
      this.selfModel = ''
      this.$emit('change', '', evt)
    },
    handleInputChange(evt){
      this.selfModel = evt.target.value
      this.$emit('update:value', evt.target.value)
      this.$emit('change', evt.target.value, evt)
    },
    handleClickIcon(evt){
      this.$emit('clickIcon', evt)
    },
    handleInputClick(evt){
      this.$emit('click', evt)
    },
    handleInputFocus(evt){
      if(!this.selectOption.focusable) evt.target.blur()
    },
    resizeTextarea(evt){
      this.$nextTick(function() {
        let currentTextarea = evt.target
        currentTextarea.style.height = '';
        currentTextarea.style.height = currentTextarea.scrollHeight + 2 + 'px'
      })
    }
  },
  mounted() {
    this.selfModel = this.value
  },
  watch: {
    value: function(val){
      this.selfModel = val
      this.$emit('update:value', val)
    }
  }
}
</script>
<style lang='scss' src="./Input.scss">

</style>



// WEBPACK FOOTER //
// Input.vue?6559adb9